import { useEffect, useMemo } from "react";
import {
  AuthenticatedArea,
  AuthPortal,
  SuiteAccountMenuControls,
  useAccessToken,
  useRememberSuiteProduct,
  useSuiteAccountAvatar,
  useTribunaleaAccess,
} from "@tribunalea/auth/react";
import {
  APPLICATION_IDS,
  canAccessApplication,
  canAccessTool,
  createFullSuiteAccess,
  TOOL_IDS,
} from "@tribunalea/shared";
import {
  InstitutionalAccessPage,
  useActivityTracking,
  useRouter,
} from "@tribunalea/ui";
import { AppShell } from "./components/AppShell.jsx";
import {
  CivitaAssistantPage,
  CivitaAssistantProvider,
} from "./components/CivitaAssistant.jsx";
import { EmptyToolPage } from "./pages/EmptyToolPage.jsx";
import { FollowCasePage } from "./pages/FollowCasePage.jsx";
import { FindLawyerPage } from "./pages/FindLawyerPage.jsx";
import { PrepareConversationPage } from "./pages/PrepareConversationPage.jsx";
import { PrepareClaimPage } from "./pages/PrepareClaimPage.jsx";
import { ReviewBeforeSigningPage } from "./pages/ReviewBeforeSigningPage.jsx";
import { UnderstandDocumentPage } from "./pages/UnderstandDocumentPage.jsx";
import { VerifyLegitimacyPage } from "./pages/VerifyLegitimacyPage.jsx";
import { createApi } from "./lib/api.js";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
export const CIVITA_DEFAULT_PATH = "/justiniano";

export const civitaRoutes = Object.freeze([
  { path: "/entender-documento", title: "Entender un documento", toolId: TOOL_IDS.CIVITA_UNDERSTAND_DOCUMENT },
  { path: "/resolver-problema", title: "Resolver un problema", toolId: TOOL_IDS.CIVITA_RESOLVE_PROBLEM },
  { path: "/revisar-antes-de-firmar", title: "Revisar antes de firmar", toolId: TOOL_IDS.CIVITA_REVIEW_BEFORE_SIGNING },
  { path: "/seguir-expediente", title: "Seguir mi expediente", toolId: TOOL_IDS.CIVITA_FOLLOW_CASE },
  { path: "/preparar-conversacion", title: "Preparar una conversación difícil", toolId: TOOL_IDS.CIVITA_PREPARE_CONVERSATION },
  { path: "/verificar-legitimidad", title: "Verificar si es legítimo", toolId: TOOL_IDS.CIVITA_VERIFY_LEGITIMACY },
  { path: "/encontrar-abogado", title: "Encontrar un abogado", toolId: TOOL_IDS.CIVITA_FIND_LAWYER },
  { path: "/justiniano", title: "Justiniano" },
]);
const defaultRoute = civitaRoutes.find(({ path }) => path === CIVITA_DEFAULT_PATH);

const legacyRoutes = Object.freeze({
  "/preparar-reclamo": "/resolver-problema",
  "/conocer-opciones": "/resolver-problema",
});

function CivitaRoutes({
  access,
  accessError = null,
  accessLoading = false,
  accountAvatar,
  accountControls,
  authEnabled,
  api,
  getAccessToken,
  reloadAccess = null,
}) {
  const { path, search, navigate } = useRouter();
  const route = civitaRoutes.find((item) => item.path === path);
  const legacyTarget = legacyRoutes[path];
  const applicationAllowed = canAccessApplication(access, APPLICATION_IDS.CIVITA);
  const routeAllowed = applicationAllowed && (
    !route?.toolId || canAccessTool(access, route.toolId)
  );
  useActivityTracking({
    apiUrl,
    getAccessToken,
    appId: APPLICATION_IDS.CIVITA,
    toolId: routeAllowed && !legacyTarget ? route?.toolId || null : null,
    enabled: authEnabled
      && !accessLoading
      && !accessError
      && routeAllowed
      && !legacyTarget,
    scopeKey: access?.workspace?.id || "personal",
  });

  useEffect(() => {
    if (!accessLoading && legacyTarget) {
      navigate(`${legacyTarget}${search}`, { replace: true });
    } else if (!accessLoading && !route) {
      navigate(CIVITA_DEFAULT_PATH, { replace: true });
    }
  }, [accessLoading, legacyTarget, navigate, route, search]);

  if (accessLoading || accessError) {
    return (
      <InstitutionalAccessPage
        error={accessError}
        loading={accessLoading}
        onRetry={reloadAccess}
      />
    );
  }

  if (legacyTarget) return null;

  return (
    <CivitaAssistantProvider getAccessToken={getAccessToken} path={route?.path || CIVITA_DEFAULT_PATH}>
      <AppShell
        access={access}
        accountAvatar={accountAvatar}
        accountControls={accountControls}
        authEnabled={authEnabled}
      >
        {routeAllowed
        ? route?.path === "/justiniano"
          ? <CivitaAssistantPage getAccessToken={getAccessToken} />
          : route?.path === "/entender-documento"
          ? (
              <UnderstandDocumentPage
                api={api}
                authEnabled={authEnabled}
              />
            )
          : route?.path === "/resolver-problema"
            ? (
                <PrepareClaimPage
                  api={api}
                  authEnabled={authEnabled}
                />
              )
          : route?.path === "/revisar-antes-de-firmar"
            ? (
                <ReviewBeforeSigningPage
                  api={api}
                  authEnabled={authEnabled}
                />
              )
          : route?.path === "/seguir-expediente"
            ? (
                <FollowCasePage
                  api={api}
                  authEnabled={authEnabled}
                />
              )
          : route?.path === "/preparar-conversacion"
            ? (
                <PrepareConversationPage
                  api={api}
                  authEnabled={authEnabled}
                />
              )
          : route?.path === "/verificar-legitimidad"
            ? (
                <VerifyLegitimacyPage
                  api={api}
                  authEnabled={authEnabled}
                />
              )
          : route?.path === "/encontrar-abogado"
            ? <FindLawyerPage api={api} />
          : <EmptyToolPage title={(route || defaultRoute).title} />
          : <InstitutionalAccessPage label={route ? "Esta herramienta" : "Civita"} />}
      </AppShell>
    </CivitaAssistantProvider>
  );
}

function ClerkApp() {
  const getAccessToken = useAccessToken();
  const accountAvatar = useSuiteAccountAvatar();
  const api = useMemo(() => createApi(getAccessToken), [getAccessToken]);
  const accessState = useTribunaleaAccess({ apiUrl });
  useRememberSuiteProduct("civita", {
    enabled: Boolean(accessState.access) && canAccessApplication(
      accessState.access,
      APPLICATION_IDS.CIVITA,
    ),
  });
  return (
    <CivitaRoutes
      access={accessState.access}
      accessError={accessState.error}
      accessLoading={accessState.loading}
      accountAvatar={accountAvatar}
      accountControls={<SuiteAccountMenuControls />}
      authEnabled
      api={api}
      getAccessToken={getAccessToken}
      reloadAccess={accessState.reload}
    />
  );
}

function SignInScreen() {
  return (
    <AuthPortal
      productName="CIVITA"
      headline="Entendé qué pasó y prepará tu próximo paso."
      description="Ingresá para trabajar con tus documentos y situaciones en un espacio personal protegido."
      proofPoints={[
        "Información organizada en lenguaje claro",
        "Tus documentos reunidos en un solo lugar",
        "Una cuenta protegida por Clerk",
      ]}
    />
  );
}

export default function App({ authEnabled }) {
  if (authEnabled) {
    return (
      <AuthenticatedArea signedOut={<SignInScreen />}>
        <ClerkApp />
      </AuthenticatedArea>
    );
  }

  return (
    <CivitaRoutes
      access={createFullSuiteAccess()}
      authEnabled={false}
      api={createApi(null)}
      getAccessToken={null}
    />
  );
}
